import React, { useState, useEffect, useContext } from 'react'
import PageHeader from '../../../components/PageHeader/PageHeader'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import API from '../../../services/API.js'
import { DataGrid } from '@mui/x-data-grid'
import VehicleColumns, { lolerCellText } from './vehicleColumns.jsx'
import { LOLER_STATE } from './VehicleComponents/Loler/lolerOptions'
import ListReportPDF from '../../../components/PDF/ListReportPDF.jsx'
import usePrintChrome from '../../../components/PDF/usePrintChrome'
import { BAD, fmtDate, GOOD, TEAL, WARN } from '../../../components/PDF/pdfKit'
import { downloadPdf } from '../../../helpers/downloadPdf'
import { useNavigate } from 'react-router-dom'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid.jsx'
import {
  Alert,
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import TableRowsIcon from '@mui/icons-material/TableRows'
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline'
import AddVehicle from '../../admin/siteConfiguration/AddVehicle.jsx'
import ResourceAvailabilityTimeline from '../../../components/ResourceTimeline/ResourceTimeline.jsx'
import moment from 'moment'
import VehicleAvailabilityPage from './VehicleAvailability.jsx'
import { UserContext } from '../../../App.js'
import useIsMobile from '../../../helpers/useIsMobile'
import { useMakeReadyTierAllowed } from '../makeReadyModule/makeReadyAccess'

// Columns hidden by default on phones so call sign, registration and status
// land in the first screen; everything else is still reachable via the grid's
// column picker or horizontal scroll.
const MOBILE_HIDDEN_COLUMNS = { image: false, baseLocation: false }

const Vehicles = () => {
  const [vehicles, setVehicles] = useState([])
  const [openAddNew, setOpenAddNew] = useState(false)
  const navigate = useNavigate()
  // UserContext holds { user, setUser } - the selection lives on the user.
  const { user } = useContext(UserContext)
  const selectedBaseLocations = user?.selectedBaseLocations
  const [orgSettingsVehicles, setOrgSettingsVehicles] = useState({})
  const makeReadyAllowed = useMakeReadyTierAllowed(2)
  const columns = VehicleColumns({
    ...orgSettingsVehicles,
    onMakeReady: makeReadyAllowed
      ? (vehicleId) => navigate(`/make-ready/${vehicleId}`)
      : undefined,
  })
  const [view, setView] = useState('Overview')
  const isMobile = useIsMobile()
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    isMobile ? MOBILE_HIDDEN_COLUMNS : {},
  )

  useEffect(() => {
    setColumnVisibilityModel(isMobile ? MOBILE_HIDDEN_COLUMNS : {})
  }, [isMobile])

  useEffect(() => {
    API.get(
      '/settings?select=vehicleSafetyCheckFrequency deepCleanFrequency vehicleServiceFrequency',
    ).then((res) => {
      if (res.status === 200) {
        setOrgSettingsVehicles(res.data)
      }
    })
  }, [])

  useEffect(() => {
    API.get('/vehicles')
      .then((res) => {
        setVehicles(res.data)
      })
      .catch((e) => console.log(e))
  }, [selectedBaseLocations])

  const chrome = usePrintChrome()

  const handlePrint = async () => {
    const statusPill = (status) => ({
      pill: {
        label: status || 'Unknown',
        color:
          status === 'Operational'
            ? GOOD
            : status === 'Out of Service'
              ? BAD
              : TEAL,
      },
    })
    const lolerCell = (row) => {
      const status = row?.lolerStatus
      if (!status) return '-'
      if (status.state === 'ok') return lolerCellText(status)
      const tone = LOLER_STATE[status.state]?.tone
      return {
        pill: {
          label: lolerCellText(status) || LOLER_STATE[status.state]?.short,
          color: tone === 'error' ? BAD : WARN,
        },
      }
    }
    // Field names match the grid columns so the user's column picker choices
    // carry through to the PDF. Image and Make Ready don't print.
    const printColumns = [
      { field: 'callSign', label: 'Call Sign', flex: 0.7, cell: (row) => row.callSign },
      { field: 'numPlate', label: 'Registration', flex: 0.7, cell: (row) => row.regPlate },
      { field: 'status', label: 'Status', flex: 0.7, cell: (row) => statusPill(row.status) },
      { field: 'vehicleType', label: 'Type', flex: 1, cell: (row) => row.vehicleType },
      {
        field: 'baseLocation',
        label: 'Location',
        flex: 1,
        cell: (row) => row.baseLocation?.name ?? '-',
      },
      {
        field: 'activeDefects',
        label: 'Defects',
        flex: 0.5,
        cell: (row) =>
          row.activeDefects > 0
            ? { pill: { label: String(row.activeDefects), color: BAD } }
            : '-',
      },
      { field: 'lolerStatus', label: 'LOLER', flex: 0.8, cell: lolerCell },
      {
        field: 'lastDeepClean',
        label: 'Last Deep Clean',
        flex: 0.8,
        cell: (row) => fmtDate(row.lastDeepClean?.end) || '-',
      },
      {
        field: 'lastSafetyCheck',
        label: 'Last Safety Check',
        flex: 0.8,
        cell: (row) => fmtDate(row.lastSafetyCheck?.testDate) || '-',
      },
      {
        field: 'lastServiceDate',
        label: 'Last Service',
        flex: 0.8,
        cell: (row) => fmtDate(row.lastServiceDate) || '-',
      },
      { field: 'motExpiry', label: 'MOT', flex: 0.8, cell: (row) => fmtDate(row.motExpiry) || '-' },
      {
        field: 'tax',
        label: 'TAX',
        flex: 0.8,
        cell: (row) => fmtDate(row.dvla?.taxDueDate) || '-',
      },
      {
        field: 'insuranceExpiry',
        label: 'Insurance',
        flex: 0.8,
        cell: (row) => fmtDate(row.insuranceExpiry) || '-',
      },
    ].filter((column) => columnVisibilityModel?.[column.field] !== false)

    const operational = vehicles.filter((v) => v.status === 'Operational').length
    const outOfService = vehicles.filter((v) => v.status === 'Out of Service').length
    const withDefects = vehicles.filter((v) => v.activeDefects > 0).length
    const baseNames = [
      ...new Set(vehicles.map((v) => v.baseLocation?.name).filter(Boolean)),
    ]

    await downloadPdf(
      <ListReportPDF
        title="Fleet List"
        subtitle={
          baseNames.length
            ? `Bases: ${
                baseNames.length <= 4
                  ? baseNames.join(', ')
                  : `${baseNames.length} selected`
              } · As of ${moment().format('D MMM YYYY')}`
            : `As of ${moment().format('D MMM YYYY')}`
        }
        docLabel="Fleet Report"
        {...chrome}
        stats={[
          { label: 'Vehicles', value: vehicles.length },
          { label: 'Operational', value: operational, color: GOOD },
          {
            label: 'Out of Service',
            value: outOfService,
            color: outOfService ? BAD : undefined,
          },
          {
            label: 'With active defects',
            value: withDefects,
            color: withDefects ? WARN : undefined,
          },
        ]}
        columns={printColumns.map(({ label, flex }) => ({ label, flex }))}
        rows={vehicles.map((row) => printColumns.map((column) => column.cell(row)))}
        emptyText="No vehicles registered"
      />,
      `AmbuNet_Fleet_List_${moment().format('YYYY-MM-DD')}.pdf`,
    )
  }

  return (
    <Stack gap={3} padding={1} component={Paper}>
      <PageHeader />
      <Stack direction="row" alignItems="center" flexWrap="wrap" rowGap={1.5}>
        <Box
          order={{ xs: 2, lg: 1 }}
          flex={1}
          display="flex"
          justifyContent="flex-start"
        >
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={(e, value) => {
              if (!value) return
              setView(value)
            }}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                fontWeight: 100,
              },
            }}
            color="contrast"
          >
            <ToggleButton value="Overview">
              <TableRowsIcon fontSize="small" sx={{ mr: 0.5 }} />
              Overview
            </ToggleButton>
            <ToggleButton value="Availability">
              <ViewTimelineIcon fontSize="small" sx={{ mr: 0.5 }} />
              Availability
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box order={3} flex={1} display="flex" justifyContent="flex-end">
          <Button
            size="small"
            variant="outlined"
            color="contrast"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddNew(true)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            New
          </Button>
        </Box>
      </Stack>
      {view === 'Availability' && (
        <VehicleAvailabilityPage vehicles={vehicles} />
      )}
      {view === 'Overview' && (
        <CustomDataGrid
          maxHeight={500}
          norowsoverlay={
            <Alert
              severity="info"
              action={
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => setOpenAddNew(true)}
                >
                  Add a Vehicle
                </Button>
              }
            >
              No vehicles registered yet. Adding your fleet unlocks VDIs,
              safety checks, cleaning schedules, defect reporting and Make
              Ready. Tip: vehicle types (Ambulance, RRV, PTS…) are managed in
              Site Configuration → Fleet → Vehicles.
            </Alert>
          }
          rows={vehicles}
          columns={columns}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          onPrint={handlePrint}
          sx={{
            '.MuiDataGrid-columnHeaderTitle': {
              fontWeight: 'bold',
            },
            '.MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-row:hover': {
              cursor: 'pointer',
            },
          }}
          disableRowSelectionOnClick
          onRowClick={({ row }) => navigate('/fleet/' + row._id)}
          slots={{
            // toolbar: () => null,
            footer: () => null,
          }}
        />
      )}
      <AddVehicle
        open={openAddNew}
        setOpen={setOpenAddNew}
        setSuccess={(newID) => navigate('/fleet/' + newID)}
      />
    </Stack>
  )
}

export default Vehicles
